import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { postService } from '../services/postService';
import ComposeTweet from '../components/ComposeTweet';
import Tweet from '../components/Tweet';

const FeedPage = () => {
  const { isAuthenticated, ensName, address } = useAuth();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [backendConnected, setBackendConnected] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const feedEndRef = useRef(null);
  
  // Reply state variables
  const [replyToPostId, setReplyToPostId] = useState(null);
  const [replyToAuthor, setReplyToAuthor] = useState('');

  // COMMENT VIEWING STATE VARIABLES
  const [viewingPostId, setViewingPostId] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  // Refs to prevent infinite loops
  const isConnectingRef = useRef(false);
  const hasAttemptedConnectionRef = useRef(false);
  const lastConnectionAttemptRef = useRef(0);

  const POSTS_PER_PAGE = 20;

  // DEBUG FUNCTION
  const debugPosts = (posts, label = 'DEBUG') => {
    console.log(`🔍 ${label} - Total posts:`, posts.length);
    posts.forEach((post, index) => {
      console.log(`🔍 ${label} - Post ${index} (${post.id}):`, {
        id: post.id,
        content: post.content?.substring(0, 50) + '...',
        author: post.author,
        hasAuthorEns: !!post.author?.ens,
        hasAuthorEnsName: !!post.author?.ensName,
        likeCount: post.likeCount,
        likedByUser: post.likedByUser,
        comment_count: post.comment_count,
        fileId: post.fileId,
        isTemp: post.isTemp
      });
    });
  };

  // Format time like Zhat
  const formatPostTime = (timeString) => {
    const time = new Date(timeString);
    const now = new Date();
    const diffMs = now - time;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    if (diffHours < 1) {
      const minutes = Math.floor(diffMs / (1000 * 60));
      return minutes < 1 ? 'just now' : `${minutes}m ago`;
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)}h ago`;
    } else if (diffHours < 168) {
      return time.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // VIEW POST WITH COMMENTS - CLEAN IMPLEMENTATION
  const viewPostWithComments = async (postId) => {
    console.log('🔍 DEBUG - Viewing post with comments:', postId);
    
    if (viewingPostId === postId) {
      // Already viewing this post - go back to feed
      goBackToFeed();
      return;
    }
    
    setViewingPostId(postId);
    setLoadingComments(true);
    
    try {
      const postComments = await postService.getComments(postId);
      console.log('🔍 DEBUG - Loaded comments:', postComments.length);
      setComments(postComments);
    } catch (error) {
      console.error('Failed to load comments:', error);
      setError('Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  };

  // GO BACK TO MAIN FEED
  const goBackToFeed = () => {
    console.log('🔍 DEBUG - Returning to feed');
    setViewingPostId(null);
    setComments([]);
  };

  // Load posts with backend/local fallback
  const loadPosts = useCallback(async (loadMore = false, forceUseBackend = false) => {
    console.log('🔍 DEBUG - loadPosts called:', { loadMore, forceUseBackend, backendConnected });
    
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    // Prevent concurrent loads ONLY if not forcing backend connection
    if (isLoading && !loadMore && !forceUseBackend) {
      return;
    }
    
    if (!loadMore) {
      setIsLoading(true);
    }
    
    setError(null);

    try {
      let loadedPosts = [];
      
      // Always use backend when forceUseBackend is true, regardless of state
      const shouldUseBackend = forceUseBackend || backendConnected;

      console.log('🔍 DEBUG - Should use backend:', shouldUseBackend);

      // Try backend first if connected or forced
      if (shouldUseBackend) {
        try {
          const currentOffset = loadMore ? offset : 0;
          console.log('🔍 DEBUG - Fetching posts from backend:', { limit: POSTS_PER_PAGE, offset: currentOffset });

          loadedPosts = await postService.getPosts(POSTS_PER_PAGE, currentOffset, false);

          console.log('🔍 DEBUG - Raw posts from backend:', loadedPosts);
          if (loadedPosts.length > 0) {
            console.log('🔍 DEBUG - First post structure:', loadedPosts[0]);
            console.log('🔍 DEBUG - First post keys:', Object.keys(loadedPosts[0]));
            console.log('🔍 DEBUG - First post author:', loadedPosts[0].author);
            console.log('🔍 DEBUG - First post has author_ens:', 'author_ens' in loadedPosts[0]);
            console.log('🔍 DEBUG - First post has author.ens:', loadedPosts[0]?.author?.ens);
          }

          // Format posts using the helper function
          const formattedPosts = loadedPosts.map(formatPost);
          console.log('🔍 DEBUG - Formatted posts:', formattedPosts);
          debugPosts(formattedPosts, 'AFTER FORMATTING');

          if (loadMore) {
            setPosts(prev => {
              const newPosts = [...prev, ...formattedPosts];
              console.log('🔍 DEBUG - Setting posts (loadMore):', newPosts.length);
              return newPosts;
            });
          } else {
            console.log('🔍 DEBUG - Setting posts (fresh):', formattedPosts.length);
            setPosts(formattedPosts);
          }

          setHasMore(loadedPosts.length === POSTS_PER_PAGE);
          if (loadMore) {
            setOffset(prev => prev + POSTS_PER_PAGE);
          }

          // Store locally as fallback
          localStorage.setItem('zpost_feed', JSON.stringify(formattedPosts));
          return;
        } catch (backendError) {
          console.error('🔍 DEBUG - Backend fetch failed:', backendError);
          // If backend fails and we were forcing it, try local storage as fallback
          if (!forceUseBackend) {
            throw backendError;
          }
        }
      }

      // Fallback to local storage
      console.log('🔍 DEBUG - Using local storage fallback');
      const savedPosts = JSON.parse(localStorage.getItem('zpost_feed') || '[]');
      console.log('🔍 DEBUG - Saved posts from localStorage:', savedPosts.length);
      
      if (loadMore) {
        // Simple pagination for local storage
        const startIndex = offset;
        const endIndex = startIndex + POSTS_PER_PAGE;
        const paginatedPosts = savedPosts.slice(startIndex, endIndex);
        
        setPosts(prev => [...prev, ...paginatedPosts]);
        setHasMore(endIndex < savedPosts.length);
        setOffset(endIndex);
      } else {
        setPosts(savedPosts);
        setHasMore(false);
        setOffset(0);
      }

    } catch (err) {
      console.error('🔍 DEBUG - loadPosts error:', err);
      setError('Failed to load posts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, backendConnected, offset, isLoading]);

      // Format a single post consistently (used for both feed and new reposts)
  const formatPost = (post) => ({
    id: post.id,
    author: {
      ensName: post.author?.ens || post.author_ens || post.author_address,
      displayName: post.author?.displayName || post.author_display_name || (post.author_ens ? post.author_ens.split('.')[0] : 'User'),
      address: post.author?.address || post.author_address
    },
    author_address: post.author?.address || post.author_address,
    author_ens: post.author?.ens || post.author_ens,
    content: post.content,
    timestamp: new Date(post.created_at).toISOString(),
    displayTime: formatPostTime(post.created_at),
    likeCount: post.likes_count || post.like_count || 0,
    repostCount: post.reposts_count || post.repost_count || 0,
    comment_count: post.comment_count || 0,
    fileId: post.file_id,
    filename: post.filename,
    originalName: post.originalName,
    type: post.fileType,
    size: post.fileSize,
    url: post.filename ? `/api/files/download/${post.filename}` : null,
    likedByUser: post.liked_by_user || false,
    repostedByUser: post.reposted_by_user || false,
    isRepost: !!post.repost_of_id,
    repost_of_id: post.repost_of_id
  });

  // Check backend connection
  const connectToBackend = useCallback(async () => {
    console.log('🔍 DEBUG - connectToBackend called');
    const now = Date.now();
    const timeSinceLastAttempt = now - lastConnectionAttemptRef.current;
    
    // Prevent rapid reconnection attempts
    if (timeSinceLastAttempt < 5000) {
      return;
    }
    
    lastConnectionAttemptRef.current = now;
    
    if (!isAuthenticated || !ensName || backendConnected || isConnectingRef.current) {
      console.log('🔍 DEBUG - Skipping connection:', { isAuthenticated, ensName, backendConnected });
      return;
    }

    isConnectingRef.current = true;
    
    try {
      console.log('🔍 DEBUG - Checking backend health...');
      const healthResult = await postService.healthCheck();
      
      if (!healthResult.success) {
        throw new Error(`Backend not accessible: ${healthResult.error}`);
      }

      console.log('🔍 DEBUG - Backend connected successfully');
      setBackendConnected(true);
      
      // Force immediate state update before loading posts
      setTimeout(() => {
        loadPosts(false, true);
      }, 0);
      
    } catch (error) {
      console.error('🔍 DEBUG - Backend connection failed:', error);
      setBackendConnected(false);
      setError('Cannot connect to posts server. Using offline mode.');
      
      // Load from local storage
      loadPosts(false);
    } finally {
      isConnectingRef.current = false;
    }
  }, [isAuthenticated, ensName, backendConnected, loadPosts]);

  // Initial load - only run once
  useEffect(() => {
    console.log('🔍 DEBUG - Initial useEffect:', { 
      isAuthenticated, 
      ensName, 
      backendConnected,
      hasAttempted: hasAttemptedConnectionRef.current
    });
    
    if (isAuthenticated && ensName && !backendConnected && !hasAttemptedConnectionRef.current) {
      hasAttemptedConnectionRef.current = true;
      connectToBackend();
    } else if (!isAuthenticated && posts.length === 0) {
      // If not authenticated and no posts, show empty state
      setIsLoading(false);
    }
  }, [isAuthenticated, ensName, backendConnected, connectToBackend, posts.length]);

  const handleNewPost = async (content, file = null, replyToPostId = null) => {
    console.log('🔍 DEBUG - handleNewPost called:', { content, file, replyToPostId, backendConnected });
    console.log('📦 file object received in handleNewPost:', file);
    console.log('📦 FULL file object in handleNewPost:', JSON.stringify(file, null, 2));

    if (!content.trim() && !file) {
      return;
    }

    if (isLoading) return;
    setIsLoading(true);

    try {
      const isReply = !!replyToPostId;
      const fileId = file?.id || null;
      const filename = file?.filename;
      const originalName = file?.originalName;
      const fileSize = file?.size;
      const fileUrl = filename ? `/api/files/download/${filename}` : null;
      const fileMimeType = file?.mimeType || null;  // ← use mimeType from the file object

      const tempPost = {
        id: `temp-${Date.now()}`,
        author: {
          ensName,
          displayName: ensName?.replace('.zpost.eth', ''),
          address
        },
        content,
        timestamp: new Date().toISOString(),
        displayTime: 'just now',
        likeCount: 0,
        repostCount: 0,
        comment_count: 0,
        fileId,
        filename,
        originalName,
        type: fileMimeType,       // ← for isImage check in Tweet.js
        fileType: fileMimeType,    // ← also set fileType (some places use it)
        size: fileSize || 0,
        url: fileUrl,
        likedByUser: false,
        repostedByUser: false,
        isTemp: true,
        isReply: !!replyToPostId,
        replyToPostId: replyToPostId
      };

      console.log('🔍 DEBUG - Created temp post:', tempPost);
      console.log('🔍 DEBUG - tempPost with file:', tempPost);

      // Add to UI immediately
      setPosts(prev => {
        const newPosts = isReply ? prev : [tempPost, ...prev];
        console.log('🔍 DEBUG - Added temp post, total:', newPosts.length);
        return newPosts;
      });

      // If this was a reply, clear reply state
      if (isReply) {
        setReplyToPostId(null);
        setReplyToAuthor('');
      }

      // Try backend
      if (backendConnected) {
        try {
          console.log('🔍 DEBUG - Calling postService.createPost with reply:', replyToPostId);
          const result = await postService.createPost(content, fileId, replyToPostId);
          console.log('🔍 DEBUG - Backend response:', result);
          console.log('📦 Backend response (full):', JSON.stringify(result, null, 2)); // optional extra log

          // Replace temp post with real one
          setPosts(prev => prev.map(p =>
            p.id === tempPost.id
              ? {
                  ...tempPost,
                  id: result.post?.id || result.id,
                  isTemp: false
                }
              : p
          ));
        } catch (backendError) {
          console.error('🔍 DEBUG - Backend create post failed:', backendError);
          // Keep temp post as permanent
          setPosts(prev => prev.map(p =>
            p.id === tempPost.id ? { ...p, isTemp: false } : p
          ));
        }
      } else {
        console.log('🔍 DEBUG - Backend not connected, keeping temp post');
        setPosts(prev => prev.map(p =>
          p.id === tempPost.id ? { ...p, isTemp: false } : p
        ));
      }

      // Save locally
      const updatedPosts = [tempPost, ...posts];
      localStorage.setItem('zpost_feed', JSON.stringify(updatedPosts.filter(p => !p.isTemp)));

    } catch (err) {
      console.error('🔍 DEBUG - handleNewPost error:', err);
      setError('Failed to create post. Please try again.');

      // Remove temp post on error
      setPosts(prev => prev.filter(p => !p.isTemp));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle like - POSITIVE-ONLY (no unlike)
  const handleLike = async (postId) => {
    console.log('🔍 DEBUG - handleLike called:', postId);

    try {
      const post = posts.find(p => p.id === postId);
      if (!post) {
        console.error('🔍 DEBUG - Post not found:', postId);
        return;
      }

      // Check if already liked - if so, do nothing (positive-only)
      if (post.likedByUser) {
        console.log('🔍 DEBUG - Post already liked, doing nothing (positive-only)');
        return;
      }

      console.log('🔍 DEBUG - Post is not liked yet, liking...');

      // Optimistic update - ONLY add, never subtract
      setPosts(prev => prev.map(p =>
        p.id === postId
          ? {
              ...p,
              likeCount: p.likeCount + 1, // Always add, never subtract
              likedByUser: true // Always set to true
            }
          : p
      ));

      // Backend call - ONLY call like, never unlike
      if (backendConnected) {
        try {
          console.log('🔍 DEBUG - Calling backend like (positive-only)');
          const result = await postService.likePost(postId); // Only like, no unlike

          if (result) {
            console.log('🔍 DEBUG - Backend response:', result);
            // Update with actual count from backend
            setPosts(prev => prev.map(p =>
              p.id === postId
                ? {
                    ...p,
                    likeCount: result.likes_count || (p.likeCount + 1),
                    likedByUser: result.liked !== false // Only false if backend says error
                  }
                : p
            ));
          }
        } catch (backendError) {
          console.error('🔍 DEBUG - Backend like failed:', backendError);
          // Revert optimistic update on error
          setPosts(posts);
        }
      } else {
        console.log('🔍 DEBUG - Backend not connected, local update only');
      }

      // Update local storage
      const updatedPosts = posts.map(p =>
        p.id === postId
          ? {
              ...p,
              likeCount: p.likeCount + 1,
              likedByUser: true
            }
          : p
      );
      localStorage.setItem('zpost_feed', JSON.stringify(updatedPosts));

    } catch (error) {
      console.error('🔍 DEBUG - handleLike error:', error);
      // Revert optimistic update
      setPosts(posts);
    }
  };

  const handleRepost = async (postId) => {
    console.log('🔍 DEBUG - handleRepost called:', postId);

    try {
      const originalPost = posts.find(p => p.id === postId);
      if (!originalPost) {
        console.error('🔍 DEBUG - Post not found for repost:', postId);
        return;
      }

      console.log('🔍 DEBUG - Calling postService.repost for', postId);
      const result = await postService.repost(postId);
      console.log('🔍 DEBUG - Raw result from postService:', result);

      if (result && result.success) {
        console.log('🔍 DEBUG - Backend response success:', result);

        if (result.reposted) {
          console.log('🔍 DEBUG - Repost created, new repost data:', result.repost);
          // --- REPOST CREATED: add the new repost post to feed ---
          if (result.repost) {
            const formattedRepost = formatPost(result.repost);
            setPosts(prev => {
              console.log('🔍 DEBUG - Adding new repost to feed, previous length:', prev.length);
              return [formattedRepost, ...prev];
            });
          }

          // Update the original post's repost count and flag
          setPosts(prev => prev.map(p =>
            p.id === postId
              ? { ...p, repostCount: result.reposts_count, repostedByUser: true }
              : p
          ));
        } else {
          console.log('🔍 DEBUG - Repost removed');
          // --- REPOST REMOVED: delete the repost post from feed ---
          const currentAddress = address; // from useAuth
          setPosts(prev => prev.filter(p => !(p.repost_of_id === postId && p.author.address === currentAddress)));

          // Update original post's repost count and flag
          setPosts(prev => prev.map(p =>
            p.id === postId
              ? { ...p, repostCount: result.reposts_count, repostedByUser: false }
              : p
          ));
        }
      } else {
        console.error('🔍 DEBUG - repost failed or returned invalid result:', result);
      }
    } catch (error) {
      console.error('🔍 DEBUG - handleRepost error:', error);
    }
  };

  // NEW: Handle cancel reply
  const handleCancelReply = () => {
    console.log('🔍 DEBUG - Cancelling reply');
    setReplyToPostId(null);
    setReplyToAuthor('');
  };

   const handleDelete = async (postId) => {
    console.log('🔍 DEBUG - handleDelete called:', postId);

    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    // Store the post in case we need to revert
    const deletedPost = posts.find(p => p.id === postId);

    try {
      // Optimistic update – remove from UI immediately
      let newPostsAfterDelete;
      setPosts(prev => {
        newPostsAfterDelete = prev.filter(p => p.id !== postId);
        console.log('🔍 DEBUG - Removed post, remaining:', newPostsAfterDelete.length);
        return newPostsAfterDelete;
      });

      // Backend call
      if (backendConnected) {
        try {
          console.log('🔍 DEBUG - Calling backend delete');
          await postService.deletePost(postId);
          console.log('🔍 DEBUG - Backend delete successful');
        } catch (backendError) {
          console.error('🔍 DEBUG - Backend delete failed:', backendError);
          // Revert the optimistic update
          if (deletedPost) {
            setPosts(prev => [deletedPost, ...prev]);
          }
          return; // Stop further processing
        }
      }

      // Update local storage with the new list (after successful backend)
      // Use setTimeout to ensure we have the latest state after setPosts
      setTimeout(() => {
        setPosts(prev => {
          localStorage.setItem('zpost_feed', JSON.stringify(prev));
          return prev;
        });
      }, 0);

    } catch (error) {
      console.error('🔍 DEBUG - handleDelete error:', error);
      // Revert on any unexpected error
      if (deletedPost) {
        setPosts(prev => [deletedPost, ...prev]);
      }
    }
  };

  // Load more posts on scroll
  const handleScroll = useCallback(() => {
    if (!feedEndRef.current || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = feedEndRef.current;
    const scrollPosition = scrollTop + clientHeight;
    const triggerPoint = scrollHeight - 100;
    
    if (scrollPosition >= triggerPoint) {
      console.log('🔍 DEBUG - Triggering load more posts');
      loadPosts(true);
    }
  }, [isLoading, hasMore, loadPosts]);

  useEffect(() => {
    const feedElement = feedEndRef.current;
    if (feedElement) {
      feedElement.addEventListener('scroll', handleScroll);
      return () => feedElement.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // DEBUG: Log posts state changes
  useEffect(() => {
    console.log('🔍 DEBUG - Posts state updated:', posts.length);
    debugPosts(posts, 'RENDER');
  }, [posts]);

  // Loading state
  if (isLoading && posts.length === 0) {
    return (
      <div className="zpost-loading" style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--black)',
        color: 'var(--text-light)'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid var(--border)',
          borderTop: '3px solid var(--gold)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '1rem'
        }}></div>
        <h3 style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>
          Loading Zpost...
        </h3>
        {!backendConnected && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            📡 Connecting to backend...
          </p>
        )}
      </div>
    );
  }

  console.log('🔍 DEBUG - Rendering FeedPage:', {
    posts: posts.length,
    isLoading,
    backendConnected,
    isAuthenticated,
    replyToPostId,
    replyToAuthor,
    viewingPostId,
    commentsCount: comments.length
  });

  return (
    <div className="zpost-feed-page">
      {/* Header */}
      <div className="zpost-feed-header" style={{
        padding: '1rem 1.5rem',
        borderBottom: '1px solid var(--border)',
        background: 'var(--black)',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ 
            color: 'var(--text-light)', 
            margin: 0, 
            fontSize: '1.5rem',
            fontWeight: 'bold'
          }}>
            {viewingPostId ? 'Comments' : 'Home'}
          </h1>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {backendConnected ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                background: 'rgba(0, 255, 136, 0.1)',
                borderRadius: '50%',
                border: '1px solid rgba(0, 255, 136, 0.3)'
              }}>
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: '#00ff88',
                  animation: 'pulse 2s infinite',
                  boxShadow: '0 0 10px rgba(0, 255, 136, 0.5)'
                }}></div>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                background: 'rgba(255, 215, 0, 0.1)',
                borderRadius: '50%',
                border: '1px solid rgba(255, 215, 0, 0.3)'
              }}>
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: 'var(--gold)',
                  animation: 'pulse 2s infinite',
                  boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
                }}></div>
              </div>
            )}
            
            {isAuthenticated && ensName && (
              <div style={{
                fontSize: '0.8rem',
                color: 'var(--gold)',
                padding: '0.25rem 0.75rem',
                background: 'rgba(255, 215, 0, 0.05)',
                borderRadius: '9999px',
                border: '1px solid rgba(255, 215, 0, 0.2)'
              }}>
                {ensName.replace('.zpost.eth', '')}
              </div>
            )}
          </div>
        </div>
        
        {!isAuthenticated && (
          <p style={{ 
            color: 'var(--text-secondary)', 
            fontSize: '0.9rem',
            marginTop: '0.5rem'
          }}>
            Connect your wallet to start posting
          </p>
        )}
      </div>
      
      {/* Error notification */}
      {error && (
        <div style={{
          margin: '1rem 1.5rem',
          padding: '0.75rem 1rem',
          background: 'rgba(128, 0, 0, 0.9)',
          color: '#ff6b6b',
          borderRadius: '8px',
          border: '1px solid #800000',
          fontSize: '0.85rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#ff6b6b',
                cursor: 'pointer',
                fontSize: '1.2rem',
                marginLeft: '0.5rem'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      {/* Compose Tweet - ALWAYS SHOW but auto-reply when viewing comments */}
      {isAuthenticated && (
        <div style={{ 
          padding: '1.5rem', 
          borderBottom: '1px solid var(--border)'
        }}>
          <ComposeTweet 
            onPost={handleNewPost} 
            isLoading={isLoading}
            currentUserENS={ensName}
            currentUserAddress={address}
            replyToPostId={viewingPostId || replyToPostId} // AUTO-REPLY when viewing comments
            replyToAuthor={viewingPostId ? 
              posts.find(p => p.id === viewingPostId)?.author?.ensName || '' : 
              replyToAuthor
            }
            onCancelReply={() => {
              if (viewingPostId) {
                // If in comment view, just clear the reply state but stay in comments
                setReplyToPostId(null);
                setReplyToAuthor('');
              } else {
                // If in main feed, clear reply and go back
                handleCancelReply();
              }
            }}
          />
        </div>
      )}
      
      {/* Feed OR Comments View */}
      <div 
        ref={feedEndRef}
        className="zpost-feed-container"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        {viewingPostId ? (
          // COMMENTS VIEW - Clean and precise
          <div>
            {/* Header with back button */}
            <div style={{
              padding: '1rem 1.5rem',
              borderBottom: '1px solid var(--border)',
              background: 'var(--black)',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <button
                onClick={goBackToFeed}
                style={{
                  background: 'none',
                  border: '1px solid var(--border)',
                  color: 'var(--text-light)',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--gold)';
                  e.currentTarget.style.color = 'var(--gold)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.color = 'var(--text-light)';
                }}
              >
                ← Back
              </button>
              <div>
                <h2 style={{ 
                  color: 'var(--text-light)', 
                  margin: 0,
                  fontSize: '1.2rem',
                  fontWeight: 'bold'
                }}>
                  Comments
                </h2>
                <div style={{
                  fontSize: '0.8rem',
                  color: 'var(--text-secondary)',
                  marginTop: '0.25rem'
                }}>
                  Post and its {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
                </div>
              </div>
            </div>
            
            {/* Main post being commented on */}
            {posts.filter(p => p.id === viewingPostId).map(post => (
              <Tweet 
                key={post.id}
                tweet={post}
                onLike={handleLike}
                onRepost={handleRepost}
                onViewComments={viewPostWithComments}
                isViewingComments={true}
                onDelete={isAuthenticated && address === post.author.address ? handleDelete : null}
                currentUserENS={ensName}
                currentUserAddress={address}
              />
            ))}
            
            {/* Comments section */}
            <div style={{ padding: '0' }}>
              {loadingComments ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: 'var(--text-secondary)'
                }}>
                  <div className="loading-spinner" style={{
                    width: '30px',
                    height: '30px',
                    border: '3px solid var(--border)',
                    borderTop: '3px solid var(--gold)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto'
                  }}></div>
                  <p style={{ marginTop: '1rem' }}>Loading comments...</p>
                </div>
              ) : comments.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: 'var(--text-secondary)',
                  borderTop: '1px solid var(--border)'
                }}>
                  <div style={{ 
                    fontSize: '2rem', 
                    color: 'var(--gold)', 
                    marginBottom: '1rem',
                    opacity: '0.3'
                  }}>
                    💬
                  </div>
                  <h3 style={{ 
                    color: 'var(--text-light)', 
                    fontSize: '1.1rem', 
                    marginBottom: '0.5rem' 
                  }}>
                    No comments yet
                  </h3>
                  <p style={{ fontSize: '0.9rem' }}>
                    Be the first to comment on this post.
                  </p>
                </div>
              ) : (
                <div>
                  <div style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid var(--border)',
                    background: 'rgba(0, 0, 0, 0.3)',
                    color: 'var(--text-secondary)',
                    fontSize: '0.85rem',
                    fontWeight: 'bold'
                  }}>
                    {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
                  </div>
                  {comments.map(comment => (
                    <Tweet 
                      key={comment.id}
                      tweet={comment}
                      onLike={handleLike}
                      onRepost={handleRepost}
                      onViewComments={viewPostWithComments}
                      onDelete={isAuthenticated && address === comment.author.address ? handleDelete : null}
                      currentUserENS={ensName}
                      currentUserAddress={address}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          // NORMAL FEED VIEW
          posts.length === 0 && !isLoading ? (
            <div className="zpost-empty-state" style={{
              textAlign: 'center',
              padding: '4rem 2rem',
              color: 'var(--text-secondary)'
            }}>
              <div style={{ 
                fontSize: '3rem', 
                color: 'var(--gold)', 
                marginBottom: '1rem',
                opacity: '0.3'
              }}>
                📝
              </div>
              <h3 style={{ 
                color: 'var(--text-light)', 
                fontSize: '1.25rem', 
                marginBottom: '0.5rem' 
              }}>
                {isAuthenticated ? 'No posts yet' : 'Welcome to Zpost'}
              </h3>
              <p style={{ fontSize: '0.95rem', maxWidth: '400px', margin: '0 auto' }}>
                {isAuthenticated 
                  ? 'Be the first to post in the .zpost.eth network. Share your thoughts, connect with others.'
                  : 'Connect your wallet with a .zpost.eth domain to start posting.'}
              </p>
            </div>
          ) : (
            <div style={{ padding: '0 0 2rem 0' }}>
              {posts.map(post => (
                <Tweet
                  key={post.id}
                  tweet={post}
                  onLike={handleLike}
                  onRepost={handleRepost}
                  onViewComments={viewPostWithComments}
                  onDelete={isAuthenticated && address?.toLowerCase() === post.author?.address?.toLowerCase() ? handleDelete : null}
                  currentUserENS={ensName}
                  currentUserAddress={address}
                />
              ))}
              
              {isLoading && posts.length > 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem',
                  color: 'var(--text-secondary)'
                }}>
                  <div className="loading-spinner" style={{
                    width: '30px',
                    height: '30px',
                    border: '3px solid var(--border)',
                    borderTop: '3px solid var(--gold)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto'
                  }}></div>
                  <p style={{ marginTop: '1rem' }}>Loading more posts...</p>
                </div>
              )}
              
              {!hasMore && posts.length > 0 && (
                <div style={{ 
                  textAlign: 'center',
                  padding: '2rem',
                  color: 'var(--text-secondary)',
                  borderTop: '1px solid var(--border)',
                  marginTop: '1rem'
                }}>
                  <p>No more posts to load</p>
                  <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                    {backendConnected ? 'You\'ve reached the end of your feed' : 'Offline mode - only showing cached posts'}
                  </p>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default FeedPage;
